import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { useEffect } from "react";
import { useNavigate, useRouteError } from "react-router-dom";

import AppIcon from "@/components/Core/AppIcon";
import EmptyState from "@/components/Core/EmptyState";
import Translations from "@/components/Core/Translations";
import { routers } from "@/configs";
import { hideBootShell } from "@/lib/bootShell";

const isStaleChunkError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error);
  return /failed to fetch dynamically imported module|error loading dynamically imported module/i.test(
    message,
  );
};

const RELOAD_GUARD_KEY = "route-error-reload";

const RouteErrorBoundary = () => {
  const error = useRouteError();
  const navigate = useNavigate();

  const alreadyReloaded = sessionStorage.getItem(RELOAD_GUARD_KEY) === "1";
  const shouldAutoReload = isStaleChunkError(error) && !alreadyReloaded;

  useEffect(() => {
    if (!shouldAutoReload) {
      hideBootShell();
      return;
    }
    sessionStorage.setItem(RELOAD_GUARD_KEY, "1");
    window.location.reload();
  }, [shouldAutoReload]);

  if (shouldAutoReload) return null;

  return (
    <Stack
      sx={{ minHeight: "calc(100vh - 150px)", p: 3 }}
      alignItems="center"
      justifyContent="center"
    >
      <Stack sx={{ maxWidth: 520, width: "100%", my: 5 }}>
        {/* These three strings used to be hard-coded English, on the one
            screen a visitor is least able to guess their way through. */}
        <EmptyState
          size="page"
          title="error.boundary-title"
          description="error.boundary-desc"
          action={
            <Stack direction="row" gap={1.5} mt={1}>
              <Button variant="outlined" onClick={() => window.location.reload()}>
                <Translations text="error.reload" />
              </Button>
              <Button
                variant="contained"
                endIcon={<AppIcon name="right2" />}
                onClick={() => navigate(routers.Home)}
              >
                <Translations text="404.btn" />
              </Button>
            </Stack>
          }
        />
      </Stack>
    </Stack>
  );
};

export default RouteErrorBoundary;
